import React, { useState, useEffect } from 'react';
import { message as antdMessage } from 'antd';
import EmbedLayout from '../../components/embed/EmbedLayout';
import ChatbotWidget from '../../components/embed/ChatbotWidget';
import { parseEmbedParams, postMessageToParent, isInIframe, parseApiError } from '../../utils/embedUtils';
import { validateEmbedConfig, mergeEmbedConfig } from '../../configs/EmbedConfig';

const ChatbotEmbed = () => {

  const [config, setConfig] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const [widgetError, setWidgetError] = useState(null);

  useEffect(() => {
    initializeEmbed();
  }, []);

  const initializeEmbed = () => {
    try {
      // Parse configuration from URL parameters
      const urlParams = parseEmbedParams();

      // Validate configuration
      const validation = validateEmbedConfig(urlParams);

      if (!validation.isValid) {
        throw new Error(`Configuration error: ${validation.errors.join(', ')}`);
      }

      // Merge with default configuration
      const finalConfig = mergeEmbedConfig(urlParams);
      setConfig(finalConfig);

      // Notify parent that embed is ready
      if (isInIframe()) {
        postMessageToParent('embed-ready', {
          config: finalConfig,
          timestamp: Date.now()
        });
      }

    } catch (error) {
      console.error('Embed initialization failed:', error);
      setWidgetError(error.message);
      
      if (isInIframe()) {
        postMessageToParent('embed-error', {
          error: error.message,
          timestamp: Date.now()
        });
      }
    }
  };

  const handleWidgetError = (error) => {
    const errorMessage = parseApiError(error, error.message);
    setWidgetError(errorMessage);
    antdMessage.error('Chat widget encountered an error');
    postMessageToParent('widget-error', {
      error: errorMessage,
      timestamp: Date.now()
    });
  };

  const handleWidgetClose = () => {
    setIsVisible(false);
    
    postMessageToParent('widget-close-requested', {
      timestamp: Date.now()
    });

    // If in iframe, we can't actually close ourselves
    // The parent needs to handle this
    if (!isInIframe()) {
      window.close();
    }
  };

  const handleWidgetMinimize = () => {
    postMessageToParent('widget-minimize-requested', {
      timestamp: Date.now()
    });
  };

  const handleMessageSent = (message) => {
    postMessageToParent('message-sent', {
      message,
      timestamp: Date.now()
    });
  };

  const handleMessageReceived = (message) => {
    postMessageToParent('message-received', {
      message,
      timestamp: Date.now()
    });
  };

  // Remove the error overlay. Always render ChatbotWidget, passing widgetError as a prop.
  if (!isVisible) {
    return (
      <EmbedLayout config={config}>
        <div style={styles.hiddenContainer}>
          <div style={styles.hiddenMessage}>Chat widget closed</div>
        </div>
      </EmbedLayout>
    );
  }

  if (!config || Object.keys(config).length === 0) {
    return (
      <EmbedLayout config={config}>
        <div style={styles.loadingContainer}>
          <div>Loading chat...</div>
        </div>
      </EmbedLayout>
    );
  }

  return (
    <EmbedLayout config={config}>
      <ChatbotWidget
        config={config}
        onError={handleWidgetError}
        onClose={handleWidgetClose}
        onMinimize={handleWidgetMinimize}
        onMessageSent={handleMessageSent}
        onMessageReceived={handleMessageReceived}
        widgetError={widgetError}
      />
    </EmbedLayout>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: '#fafafa',
    fontSize: '14px',
    color: '#666'
  },

  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '32px',
    textAlign: 'center',
    backgroundColor: '#fafafa'
  },
  
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  
  errorTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px'
  },
  
  errorMessage: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px',
    maxWidth: '300px',
    lineHeight: '1.5'
  },
  
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#1890ff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  
  hiddenContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: '#fafafa'
  },
  
  hiddenMessage: {
    fontSize: '14px',
    color: '#999',
    fontStyle: 'italic'
  }
};

export default ChatbotEmbed;
